import { createAnalyticsMixpanel } from '@tumelo/analytics'
import { GlobalStyle, SoftConfigThemeSchema } from '@tumelo/designsystem'
import {
  LoggerProvider,
  LoggerService,
  LoggerServiceCompound,
  LoggerServiceConsole,
  LoggerServiceSentry,
} from '@tumelo/logging'
import { useLocalStorage } from '@tumelo/react'
import { AppProps } from 'next/app'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { ThemeProvider } from 'styled-components'
import * as Analytics from '../application/analytics'
import { Auth } from '../application/auth'
import { EmailVerificationWrapper } from '../components/EmailVerification'
import { ClientContext, getMockedOrNotMockedClient } from '../application/client'
import { ConfigContext, getConfig } from '../application/environment'
import { FlagsContextProvider } from '../application/FlagsContext'
import { PrivilegesContextProvider } from '../application/PrivilegesContext'
import {
  ClientContext as ReportingClientContext,
  getMockedOrNotMockedReportingClient,
} from '../application/reportingClient'
import { AmplifyUserCtx, sampleUserContext, UserContext } from '../application/user'
import { CookieBanner, cookieConsentKey, CookieConsentValue } from '../components/cookies'
import { Landing } from '../components/landing'
import { PlannedMaintenance } from '../components/PlannedMaintenance'
import { SchemeGuard } from '../components/SchemeGuard'
import '../styles/globals.css'
import { getMonolithConfig } from '../application/monolithEnvironment'
import { getMockedOrNotMockedMonolithClient, MonolithClientContext } from '../application/monolithClient'

// get config at top level to ensure throwing an error during build if env vars are missing
getConfig()

export default function App({ Component, pageProps }: AppProps) {
  const [cookieConsent] = useLocalStorage(cookieConsentKey, CookieConsentValue.NotSet)
  const cookiesAccepted = cookieConsent === CookieConsentValue.Accepted
  const config = getConfig()
  const ptvConfig = getMonolithConfig()
  const router = useRouter()
  const bypassMaintenance = router.query.bypass === 'true'
  const isProduction = config.appEnv === 'production'
  const userContext = config.clientType === 'mock' ? sampleUserContext() : AmplifyUserCtx()
  const getClient = getMockedOrNotMockedClient(config)
  const getPTVClient = getMockedOrNotMockedMonolithClient(ptvConfig)
  const getReportingClient = getMockedOrNotMockedReportingClient(config)

  // Configure logger service
  let logger: LoggerService = LoggerServiceConsole
  const dsn = 'https://73860044b4f04b5b98f5b8080f453ae0@o365838.ingest.sentry.io/5958341'
  const sentryLogger = new LoggerServiceSentry({ dsn, environment: config.appEnv, enableFeedbackWidget: true })
  let mixpanelAnalytics = Analytics.useAnalytics()

  if (isProduction && cookiesAccepted) {
    logger = new LoggerServiceCompound(LoggerServiceConsole, sentryLogger)
    // Stewardship Platform Prod mixpanel
    mixpanelAnalytics = createAnalyticsMixpanel('9db3041cca89e463a1478854fb15b33d', {})
  }

  if (!isProduction && cookiesAccepted) {
    // Stewardship Platform Dev mixpanel
    mixpanelAnalytics = createAnalyticsMixpanel('7c74f88bbb9d027882b55ae2519c3af5', { debug: true, ignore_dnt: true })
  }

  useEffect(() => {
    const setUserId = async () => {
      const user = await userContext.getUserDetails()
      if (user === undefined) {
        logger.setUserData({})
      } else {
        logger.setUserData({ id: user.id })
      }
    }
    setUserId()
  }, [logger, userContext])

  if (config.plannedMaintenance && !bypassMaintenance) {
    return <PlannedMaintenance />
  }

  return (
    <>
      <Head>
        <title>Tumelo Stewardship Dashboard</title>
        <link rel="icon" href="/favicon-32x32.png" />
      </Head>
      <LoggerProvider logger={logger}>
        <Analytics.AnalyticsContext value={mixpanelAnalytics}>
          <ConfigContext value={config}>
            <ClientContext value={getClient}>
              <ReportingClientContext value={getReportingClient}>
                <MonolithClientContext value={getPTVClient}>
                  <ThemeProvider theme={SoftConfigThemeSchema.parse()}>
                    <Auth>
                      <EmailVerificationWrapper>
                        <GlobalStyle />
                        <Landing>
                          <Analytics.PageViewFromLocationPathname>
                            <Analytics.SetUserProperties>
                              <CookieBanner>
                                <UserContext value={userContext}>
                                  <FlagsContextProvider>
                                    <PrivilegesContextProvider>
                                      <SchemeGuard>
                                        <Component {...pageProps} />
                                      </SchemeGuard>
                                    </PrivilegesContextProvider>
                                  </FlagsContextProvider>
                                </UserContext>
                              </CookieBanner>
                            </Analytics.SetUserProperties>
                          </Analytics.PageViewFromLocationPathname>
                        </Landing>
                      </EmailVerificationWrapper>
                    </Auth>
                  </ThemeProvider>
                </MonolithClientContext>
              </ReportingClientContext>
            </ClientContext>
          </ConfigContext>
        </Analytics.AnalyticsContext>
      </LoggerProvider>
    </>
  )
}
